import { motion } from "framer-motion";
import { Play, Youtube, Linkedin, ClipboardCheck, Wrench, FileText, Settings } from "lucide-react";
import { useApp } from "@/context/AppProviders";
import { YOUTUBE_URL, LINKEDIN_URL } from "@/config";

const chips = [ClipboardCheck, Wrench, FileText, Settings];

const Academy = () => {
  const { t } = useApp();
  const a = t.academy;

  return (
    <section
      id="academy"
      data-testid="academy-section"
      className="py-24 sm:py-32 border-t border-border"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl"
        >
          <p className="font-mono text-sm uppercase tracking-widest text-blue-500">
            {a.label}
          </p>
          <h2 className="font-heading mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
            {a.title}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            {a.subtitle}
          </p>
        </motion.div>

        <div className="mt-12 grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-center">
          {/* Video thumbnail card */}
          <motion.a
            href={YOUTUBE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="academy-video-card"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
            className="group relative block overflow-hidden rounded-sm border border-border bg-card"
          >
            <div className="relative aspect-video overflow-hidden">
              <img
                src="/academy-thumb.png"
                alt={a.videoTitle}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-600/90 text-white shadow-[0_0_40px_-6px_rgb(37,99,235)] transition-transform duration-300 group-hover:scale-110">
                  <Play className="h-7 w-7 translate-x-0.5 fill-current" />
                </span>
              </div>
            </div>
            <div className="border-t border-border p-5">
              <h3 className="font-heading text-base font-semibold tracking-tight">
                {a.videoTitle}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {a.videoDesc}
              </p>
            </div>
          </motion.a>

          {/* Info + CTA */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex flex-col"
          >
            <div className="grid grid-cols-2 gap-3">
              {chips.map((Icon, idx) => (
                <div
                  key={idx}
                  data-testid={`academy-chip-${idx}`}
                  className="flex items-center gap-2.5 rounded-sm border border-border bg-card px-3.5 py-3 text-sm font-medium"
                >
                  <Icon className="h-4 w-4 flex-shrink-0 text-blue-500" />
                  {a.chips[idx]}
                </div>
              ))}
            </div>

            <a
              href={YOUTUBE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="academy-youtube-cta"
              className="mt-6 inline-flex h-11 items-center justify-center gap-2 rounded-sm bg-blue-600 px-5 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-500"
            >
              <Youtube className="h-4 w-4" />
              {a.cta}
            </a>
            <p className="mt-3 text-xs text-muted-foreground/70">{a.channelNote}</p>

            <div className="mt-6 flex items-start gap-3 rounded-sm border border-border bg-card p-4">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-sm bg-[#0a66c2] text-white">
                <Linkedin className="h-4 w-4" strokeWidth={2} />
              </span>
              <div className="min-w-0">
                <a
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="academy-linkedin-cta"
                  className="text-sm font-medium text-foreground transition-colors hover:text-blue-500"
                >
                  {a.linkedinCta}
                </a>
                <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                  {a.linkedinNote}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Academy;
